import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useSelector } from 'react-redux';
import { Card, Tabs, Tab, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ClaimSelectModal from './ClaimSelectModal';
import DataCenterDataType from './DataCenterDataType';
import DataCenterExportTab from '@rd-web-markets/shared/dist/data_centre/DataCenterExportTab';
import claimTemplateImportsService from './claim_template_imports.service';
import ClaimTemplateImportsTable from './ClaimTemplateImportsTable';
import importCostTemplateService from '@services/claim/import_cost_template.service';
import { buildFormData } from '@rd-web-markets/shared/dist/services/service';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import { UPLOAD_COST_TEMPLATE_BUTTON_NAME } from '@rd-web-markets/market/dist/constants';
import { SHOW_DOWNLOAD_TEMPLATE_BUTTON } from '@rd-web-markets/market/dist/constants';
import { setBreadcrumb, clearBreadcrumb } from '@rd-web-markets/shared/dist/store/features/navbarBreadcrumbSlice';
import { useDispatch } from 'react-redux';
const DataCentreMainPage = _ref => {
  let {
    claimGroup,
    canUploadTemplate,
    hideExportTab = false,
    hideTabs = false
  } = _ref;
  const history = useHistory();
  const accountType = useSelector(state => state.auth.user.account_type);
  const [showClaimSelectModal, setShowClaimSelectModal] = useState(false);
  const [targetPage, setTargetPage] = useState('');
  const [activeTab, setActiveTab] = useState('load_data');
  const [importsLoading, setImportsLoading] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState(claimGroup.claims[0]?.id);
  const {
    t
  } = useTranslation();
  const isUnderReview = !!claimGroup.active_change_set;
  const [costUploadLoading, setCostUploadLoading] = useState(false);
  const [costDataContainsProjects, setCostDataContainsProjects] = useState(false);
  const hiddenCostFileInputRef = useRef(null);
  const {
    imports,
    setImports
  } = claimTemplateImportsService.useCrud(setImportsLoading, selectedClaimId, claimGroup.id);
  const dispatch = useDispatch();
  const handleUploadCostTemplate = useErrorHandling(useCallback(async (file, withProjects) => {
    const fd = buildFormData({
      file,
      with_projects: withProjects
    });
    setCostUploadLoading(true);
    const response = await importCostTemplateService.create(selectedClaimId, fd, null);
    setImports(prevImports => [{
      ...response,
      import_type: 'cost'
    }, ...prevImports]);
    setCostUploadLoading(false);
  }, [selectedClaimId, setImports]), () => setCostUploadLoading(false));
  useEffect(() => {
    dispatch(setBreadcrumb({
      label: claimGroup.company.name,
      path: `/${accountType}/companies/${claimGroup.company.id}/overview`
    }));
    return () => dispatch(clearBreadcrumb());
  }, [claimGroup.id, accountType, dispatch]);
  const handleCostUploadButtonClick = withProjects => {
    setCostDataContainsProjects(withProjects);
    hiddenCostFileInputRef.current.click();
  };
  const onCostFileSelected = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      window.alert(t('please_upload_xlsx_file'));
      e.target.value = '';
      return;
    }
    await handleUploadCostTemplate(file, costDataContainsProjects);
    e.target.value = '';
  };
  const getSelectedClaimUrl = (targetPage, claimId) => {
    switch (targetPage) {
      case 'clients_cost_data':
        return `/${accountType}/claim_groups/${claimGroup.id}/data_centre/clients_cost_data_progress?claim_id=${claimId}`;
      case 'advance_cost_template':
        return `/${accountType}/claims/${claimId}/financial_details/upload`;
      default:
        console.error('this page does not exist: ');
    }
  };
  const goToProgressPage = targetPage => {
    let url;
    const shouldShowClaimSelect = claimGroup.claims.length > 1;
    setTargetPage(targetPage);
    switch (targetPage) {
      case 'clients_cost_data':
        if (shouldShowClaimSelect) {
          setShowClaimSelectModal(true);
          return;
        }
        url = getSelectedClaimUrl(targetPage, claimGroup.claims[0].id);
        break;
      case 'advance_cost_template':
        if (shouldShowClaimSelect) {
          setShowClaimSelectModal(true);
          return;
        }
        url = getSelectedClaimUrl(targetPage, claimGroup.claims[0].id);
        break;
      case 'project_template':
        url = `/${accountType}/claim_groups/${claimGroup.id}/data_centre/project_import_progress`;
        break;
      case 'upload_technical_narrative':
        url = `/${accountType}/claim_groups/${claimGroup.id}/upload_narrative_template`;
        break;
      case 'upload_rnd_report':
        url = `/${accountType}/claim_groups/${claimGroup.id}/upload_rnd_report`;
        break;
      default:
        console.error('this page does not exist: ', targetPage);
        return;
    }
    history.push(url);
  };
  const costUploadButtonsSettings = [{
    buttonId: 'upload-clients_cost-template-button',
    progressPageString: 'clients_cost_data',
    text: t('upload_client_cost_data'),
    disabled: canUploadTemplate || claimGroup.staffing_cost_type === 'rapid'
  }, {
    buttonId: 'upload-cost-template-button',
    text: t(UPLOAD_COST_TEMPLATE_BUTTON_NAME),
    disabled: costUploadLoading,
    onClick: () => handleCostUploadButtonClick(false)
  }];
  if (process.env.PUBLIC_URL === '/uk') {
    costUploadButtonsSettings.push({
      buttonId: 'upload-cost-template-with-projects-button',
      text: 'Upload Cost Template With Projects',
      disabled: costUploadLoading,
      onClick: () => handleCostUploadButtonClick(true)
    });
  }
  const loadDataContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DataCenterDataType, {
    canUploadTemplate: canUploadTemplate,
    goToProgressPage: goToProgressPage,
    typeName: "Cost",
    uploadButtonsSettings: costUploadButtonsSettings
  }), /*#__PURE__*/React.createElement(DataCenterDataType, {
    canUploadTemplate: canUploadTemplate,
    goToProgressPage: goToProgressPage,
    typeName: "Project",
    uploadButtonsSettings: [{
      buttonId: 'project_template_btn',
      progressPageString: 'project_template',
      text: t('upload_project_template')
    }, {
      buttonId: 'upload_technical_narrative',
      progressPageString: 'upload_technical_narrative',
      text: t('upload_technical_narrative')
    }]
  }), /*#__PURE__*/React.createElement(DataCenterDataType, {
    canUploadTemplate: canUploadTemplate,
    goToProgressPage: goToProgressPage,
    typeName: "R&D Report",
    uploadButtonsSettings: [{
      buttonId: 'upload_rnd_report_button',
      progressPageString: 'upload_rnd_report',
      text: t('upload_rnd_report')
    }]
  }), /*#__PURE__*/React.createElement("hr", {
    className: "my-4"
  }), /*#__PURE__*/React.createElement("h6", {
    className: "text-primary mb-3"
  }, t('import_history')), claimGroup.claims.length > 1 && /*#__PURE__*/React.createElement(Form.Select, {
    className: "mb-3",
    style: {
      width: '240px'
    },
    value: selectedClaimId,
    onChange: e => setSelectedClaimId(Number(e.target.value))
  }, claimGroup.claims.map(claim => /*#__PURE__*/React.createElement("option", {
    key: claim.id,
    value: claim.id
  }, claim.name))), /*#__PURE__*/React.createElement(ClaimTemplateImportsTable, {
    imports: imports,
    loading: importsLoading
  }));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClaimSelectModal, {
    show: showClaimSelectModal,
    onHide: () => setShowClaimSelectModal(false),
    claims: claimGroup.claims,
    title: 'Select a claim',
    getSelectedClaimUrl: getSelectedClaimUrl,
    targetPage: targetPage
  }), /*#__PURE__*/React.createElement("input", {
    style: {
      display: 'none'
    },
    type: "file",
    "data-testid": "costTemplateUpload",
    ref: hiddenCostFileInputRef,
    onChange: onCostFileSelected
  }), isUnderReview && /*#__PURE__*/React.createElement(Alert, {
    key: "warning",
    variant: "warning"
  }, "Claim info can not be edited after manager or client review has started"), /*#__PURE__*/React.createElement("h2", {
    className: "mt-3 mb-5 text-primary"
  }, "Data Center"), /*#__PURE__*/React.createElement("style", null, `
        .DataCenterDataType:not(:first-of-type) { margin-top: 20px; }
      `), /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Card.Body, {
    className: "w-100 p-3"
  }, hideTabs ? loadDataContent : /*#__PURE__*/React.createElement(Tabs, {
    activeKey: activeTab,
    onSelect: k => setActiveTab(k),
    id: "data_center_tabs",
    className: activeTab === 'export_data' ? 'w-100' : ''
  }, /*#__PURE__*/React.createElement(Tab, {
    eventKey: "load_data",
    title: t('load_data'),
    className: "mb-2"
  }, loadDataContent), !hideExportTab && /*#__PURE__*/React.createElement(Tab, {
    eventKey: "export_data",
    title: t('export_data')
  }, /*#__PURE__*/React.createElement(DataCenterExportTab, {
    claimGroup: claimGroup,
    showDownloadTemplateButton: SHOW_DOWNLOAD_TEMPLATE_BUTTON
  }))))));
};
export default DataCentreMainPage;